import type { ComponentType } from "react";
import type { LucideProps } from "lucide-react";

export interface ToolRailItem<T extends string> {
    id: T;
    label: string;
    Icon: ComponentType<LucideProps>;
    /** Optional accent color for the active indicator (defaults to theme accent). */
    accent?: string;
    /** Optional keyboard shortcut, shown in the button's tooltip (e.g. "V"). */
    hotkey?: string;
}

interface ToolRailProps<T extends string> {
    /** Accessible label for the rail (e.g. "Diagram subtools"). */
    ariaLabel: string;
    items: ReadonlyArray<ToolRailItem<T>>;
    activeId: T;
    onSelect: (id: T) => void;
}

/**
 * Generic vertical icon rail for per-tool subtool selection. Sits flush
 * against a tool's canvas (rendered inside the viewer panel). Presentational
 * only — owns no state; the host tool drives `activeId` / `onSelect`.
 *
 * Reusable across tools: pass an item list of `{ id, label, Icon }` plus the
 * active id and a select handler.
 */
export function ToolRail<T extends string>({
    ariaLabel,
    items,
    activeId,
    onSelect,
}: ToolRailProps<T>) {
    return (
        <div className="tool-rail" role="toolbar" aria-label={ariaLabel} aria-orientation="vertical">
            {items.map(({ id, label, Icon, accent, hotkey }) => {
                const active = id === activeId;
                const title = hotkey ? `${label} (${hotkey})` : label;
                return (
                    <button
                        key={id}
                        type="button"
                        className={`tool-rail-btn${active ? " active" : ""}`}
                        style={
                            accent
                                ? ({ "--tool-rail-accent": accent } as React.CSSProperties)
                                : undefined
                        }
                        onClick={() => onSelect(id)}
                        title={title}
                        aria-label={title}
                        aria-pressed={active}
                    >
                        <Icon className="tool-rail-icon" size={18} aria-hidden />
                    </button>
                );
            })}
        </div>
    );
}
